/**
 * The workspace's LaterViewModel (one per signed-in workspace, made by WorkspaceRoot), found by the Later screen, the
 * sidebar badge and the conversations' message menus without threading another context through the shell.
 */
import type { EmojiUsageViewModel, LaterViewModel, ScheduledViewModel, WorkspaceViewModel } from "@bundle/core";
import { useWorkspace } from "../hooks/index.ts";
import { useSyncExternalStore } from "react";

const byWorkspace = new Map<WorkspaceViewModel, LaterViewModel>();
const scheduledByWorkspace = new Map<WorkspaceViewModel, ScheduledViewModel>();
const emojiByWorkspace = new Map<WorkspaceViewModel, EmojiUsageViewModel>();
const listeners = new Set<() => void>();
const changed = () => {
  for (const l of listeners) l();
};

/** WorkspaceRoot: makes `later` the workspace's (until the returned function runs). */
export function registerLater(ws: WorkspaceViewModel, later: LaterViewModel): () => void {
  byWorkspace.set(ws, later);
  changed();
  return () => {
    if (byWorkspace.get(ws) === later) byWorkspace.delete(ws);
    changed();
  };
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/** This workspace's Later view model (undefined outside a workspace shell). */
export function useLater(): LaterViewModel | undefined {
  const ws = useWorkspace();
  return useSyncExternalStore(subscribe, () => byWorkspace.get(ws.vm));
}

/** WorkspaceRoot: makes `scheduled` the workspace's scheduled-messages view model. */
export function registerScheduled(ws: WorkspaceViewModel, scheduled: ScheduledViewModel): () => void {
  scheduledByWorkspace.set(ws, scheduled);
  changed();
  return () => {
    if (scheduledByWorkspace.get(ws) === scheduled) scheduledByWorkspace.delete(ws);
    changed();
  };
}

/** This workspace's scheduled messages view model (undefined outside a workspace shell). */
export function useScheduled(): ScheduledViewModel | undefined {
  const ws = useWorkspace();
  return useSyncExternalStore(subscribe, () => scheduledByWorkspace.get(ws.vm));
}

/** WorkspaceRoot: makes `usage` the workspace's "Frequently used" emoji view model. */
export function registerEmojiUsage(ws: WorkspaceViewModel, usage: EmojiUsageViewModel): () => void {
  emojiByWorkspace.set(ws, usage);
  changed();
  return () => {
    if (emojiByWorkspace.get(ws) === usage) emojiByWorkspace.delete(ws);
    changed();
  };
}

export function useEmojiUsage(): EmojiUsageViewModel | undefined {
  const ws = useWorkspace();
  return useSyncExternalStore(subscribe, () => emojiByWorkspace.get(ws.vm));
}
